import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { AddShiftTemplateData, AddWorkDayConfigData, AddWorkWeekConfigData, AdministrationResultStatus, ConfigurationParameterData, DayData, ShiftTemplateData, UpdateShiftTemplateData, WorkDayConfigAvailabilityData, WorkDayConfigData, WorkWeekConfigData, WorkingHoursDailyConfigData } from 'src/app/clients/system-api/SystemApiClient';
import { ConfigurationParameterService } from 'src/app/services/configuration-parameter.service';
import { ShiftTemplateService } from 'src/app/services/shift-template.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { cloneDeep } from 'lodash'; // Import the cloneDeep function from lodash
import { WindowDimensions, WindowDimensionsService } from 'src/app/services/window-dimension-service.service';

const DEFAULT_SCROLL_DURATION = 200;

interface AddWorkWeekConfigDataItem {
  id: number;
  workWeekConfigData: AddWorkWeekConfigData;
  removed: boolean;
}

@Component({
  selector: 'app-shift-template-config-popup',
  templateUrl: './shift-template-config-popup.component.html',
  styleUrls: ['./shift-template-config-popup.component.scss'],
})
export class ShiftTemplateConfigPopupComponent implements OnInit, AfterViewInit {
  @Input() shiftTemplateId?: number = undefined;
  @Input() editingMode?: boolean = false;

  @Output() close = new EventEmitter<boolean>();

  windowDimensions: WindowDimensions = {} as WindowDimensions;

  stateClose: boolean = false;
  dayData: DayData[] | undefined = [];

  addShiftTemplatePopupState: boolean = false;
  currentConfigurationData?: ConfigurationParameterData;
  originalWorkingHourConfigs?: WorkingHoursDailyConfigData[];
  sortedWorkingHours: WorkingHoursDailyConfigData[][] = [];
  hasConfiguration: boolean = false;
  editWeekConfigs: boolean = false;
  canRemoveWeeks: boolean = true;
  removedWeeks: number = 0;

  baseWorkWeekConfig: AddWorkWeekConfigData = new AddWorkWeekConfigData({ workDaysConfig: [] });
  workWeekConfigs: AddWorkWeekConfigDataItem[] = [];
  currentWeekPosition: number = 0;

  originalEditingScheduleData: ShiftTemplateData | undefined = undefined;
  baseShiftTemplateData: AddShiftTemplateData = new AddShiftTemplateData();
  addWorkWeekConfigData: AddWorkWeekConfigData = new AddWorkWeekConfigData();
  hasBookedTermsInChange: boolean = false;

  @ViewChild('overflowContainer') overflowContainer!: ElementRef;

  constructor(private shiftTemplateService: ShiftTemplateService,
    private _snackBarService: SnackBarService,
    private translateService: TranslateService,
    private configurationParameterService: ConfigurationParameterService,
    private _windowDimensionService: WindowDimensionsService) {
    this._windowDimensionService.getWindowDimensions$().subscribe((dimensions) => {
      this.windowDimensions = dimensions;
    });
  }

  ngOnInit(): void { this.dataInit(); }

  ngAfterViewInit(): void { this.scrollToEnd(); }

  public async dataInit(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      this.configurationParameterService
        .retrieveActiveConfigurationParameter()
        .subscribe((data) => {
          if (data.status === AdministrationResultStatus.Success) {
            this.currentConfigurationData = data.content;
            if (this.currentConfigurationData?.workingHours && this.currentConfigurationData?.workingHours.length > 0) {
              this.originalWorkingHourConfigs = this.currentConfigurationData?.workingHours!;
              this.currentConfigurationData?.workingHours!.forEach((config) => {
                var addWorkDayConfig: AddWorkDayConfigData = new AddWorkDayConfigData({
                  dayOfWeek: config.dayOfWeek, fromTime: config.fromTime, toTime: config.toTime,
                  working: config.working, enabled: true,
                });

                this.baseWorkWeekConfig.workDaysConfig?.push(addWorkDayConfig);
              });
            }

            if (!this.hasConfiguration) { this.hasConfiguration = true; }

            if ((this.editingMode === true && this.shiftTemplateId !== undefined) || this.shiftTemplateId !== undefined) {
              this.shiftTemplateService.retrieveShiftTemplateById(this.shiftTemplateId).subscribe((data) => {
                if (data.status === AdministrationResultStatus.Success && data.content !== undefined) {
                  let newAddScheduleTemplateData: AddShiftTemplateData = new AddShiftTemplateData({
                    description: data.content?.description, name: data.content?.name,
                    startingFrom: data.content?.startingFrom, endOn: data.content?.endOn,
                    workWeekList: [],
                  });

                  if (data.content.workWeekList !== undefined && data.content.workWeekList.length > 0) {
                    data.content?.workWeekList?.forEach((weekConfig, _index) => {
                      let newAddWorkWeekConfigData: AddWorkWeekConfigData = new AddWorkWeekConfigData();
                      newAddWorkWeekConfigData.workDaysConfig = [];
                      if (weekConfig.workDaysConfig !== undefined) {
                        Object.keys(weekConfig.workDaysConfig).forEach((key) => {
                          let selectedConfig = weekConfig.workDaysConfig![key] as WorkDayConfigData[];
                          selectedConfig.forEach((config) => {
                            let addWorkDayConfigData: AddWorkDayConfigData = new AddWorkDayConfigData({
                              dayOfWeek: config.dayOfWeek, fromTime: config.fromTime, toTime: config.toTime,
                              working: config.working, enabled: true,
                            });

                            const deepCopy = cloneDeep(addWorkDayConfigData);
                            newAddWorkWeekConfigData.workDaysConfig?.push(deepCopy);
                          });
                        });
                      }

                      const deepCopy = cloneDeep(newAddWorkWeekConfigData);
                      this.workWeekConfigs.push({
                        id: this.workWeekConfigs.length,
                        workWeekConfigData: deepCopy,
                        removed: false
                      } as AddWorkWeekConfigDataItem);
                    });

                    newAddScheduleTemplateData.workWeekList = this.workWeekConfigs.map((weekConfig) => weekConfig.workWeekConfigData);
                  }

                  this.currentWeekPosition = 0;
                  this.baseShiftTemplateData = newAddScheduleTemplateData;
                  this.originalEditingScheduleData = data.content;
                } else {
                  this.addBaseConfig(); this.editingMode = false; this.shiftTemplateId = undefined;
                }
              });
            } else {
              this.addBaseConfig();
            }

            resolve();
          } else {
            this.hasConfiguration = false;
            reject();
          }
        });


    });
  }

  private addBaseConfig() {
    let newAddWorkWeekConfigData: AddWorkWeekConfigData = new AddWorkWeekConfigData();
    if (this.baseWorkWeekConfig.workDaysConfig && this.baseWorkWeekConfig.workDaysConfig?.length > 0) {
      newAddWorkWeekConfigData.workDaysConfig = [...this.baseWorkWeekConfig.workDaysConfig];
    }

    const deepCopy = cloneDeep(newAddWorkWeekConfigData);

    this.workWeekConfigs.push({
      id: 0,
      workWeekConfigData: deepCopy,
      removed: false
    } as AddWorkWeekConfigDataItem);
  }

  public createEmptyWeekConfig() {
    if (this.editingMode !== true || this.shiftTemplateId === undefined) {
      let newAddWorkWeekConfigData: AddWorkWeekConfigData = new AddWorkWeekConfigData();
      newAddWorkWeekConfigData.workDaysConfig = [];
      if (this.baseWorkWeekConfig.workDaysConfig && this.baseWorkWeekConfig.workDaysConfig?.length > 0) {
        newAddWorkWeekConfigData.workDaysConfig = [...this.baseWorkWeekConfig.workDaysConfig];
      }

      const deepCopy = cloneDeep(newAddWorkWeekConfigData);
      this.workWeekConfigs.push({
        id: this.workWeekConfigs.length,
        workWeekConfigData: deepCopy,
        removed: false
      } as AddWorkWeekConfigDataItem);

      this.currentWeekPosition = this.workWeekConfigs.length - 1;

      setTimeout(() => { this.scrollToEnd(); }, 50);
    }
  }

  public toggleWeekToRemove(week: AddWorkWeekConfigDataItem) {
    if (this.editingMode !== true || this.shiftTemplateId === undefined) {
      if (week.removed === true) {
        if (this.workWeekConfigs.length - (--this.removedWeeks) > 1) { this.canRemoveWeeks = true; }
      } else {
        if (this.canRemoveWeeks) {
          if (this.workWeekConfigs.length - (++this.removedWeeks) <= 1) { this.canRemoveWeeks = false; }
        }
      }
      week.removed = !week.removed;
    }
  }

  public removeWeekConfigs() {
    if (this.editingMode !== true || this.shiftTemplateId === undefined) {
      if (this.workWeekConfigs.length > 1 && this.removedWeeks > 0) {
        this.workWeekConfigs = this.workWeekConfigs.filter((item) => item.removed !== true);
        this.currentWeekPosition = this.currentWeekPosition < this.workWeekConfigs.length - 1 ? this.currentWeekPosition : this.workWeekConfigs.length - 1;
        if (this.workWeekConfigs.length === 1) {
          this.currentWeekPosition = 0;
        }
      }

      this.editWeekConfigs = false; this.canRemoveWeeks = false; this.removedWeeks = 0;
    }
  }

  public toggleWeekEdit() {
    if (this.editingMode !== true || this.shiftTemplateId === undefined) {
      this.editWeekConfigs = !this.editWeekConfigs;
      if (!this.editWeekConfigs) {
        this.removeWeekConfigs();
      } else {
        this.canRemoveWeeks = true;
        this.removedWeeks = 0;
      }
    }
  }

  public closeShiftPopup() {
    this.workWeekConfigs = []; this.originalWorkingHourConfigs = []; this.sortedWorkingHours = [];
    this.hasConfiguration = false; this.addShiftTemplatePopupState = false; this.canRemoveWeeks = true;
    this.currentWeekPosition = 0; this.removedWeeks = 0;
    this.close.emit();
  }

  public commitShiftTemplate() {
    if (this.workWeekConfigs !== undefined && this.workWeekConfigs.length > 0) {
      let currentWorkWeekConfigs = cloneDeep(this.workWeekConfigs);

      if (!((this.editingMode === true && this.shiftTemplateId !== undefined) || this.shiftTemplateId !== undefined)) {
        this.baseShiftTemplateData.workWeekList = [];

        currentWorkWeekConfigs.forEach((weekConfig) => {
          weekConfig.workWeekConfigData.workDaysConfig?.forEach((dayConfig) => {
            dayConfig.fromTime = moment(dayConfig.fromTime).utc(true).toDate();
            dayConfig.toTime = moment(dayConfig.toTime).utc(true).toDate();
          });
          this.baseShiftTemplateData.workWeekList?.push(weekConfig.workWeekConfigData);
        });

        this.baseShiftTemplateData.startingFrom = moment(new Date()).utc(true).toDate();
        this.baseShiftTemplateData.endOn = moment(new Date()).utc(true).toDate();
        this.baseShiftTemplateData.endOn.setDate(this.baseShiftTemplateData.endOn.getDate() + (7 * this.baseShiftTemplateData.workWeekList!.length));

        this.shiftTemplateService.addShiftTemplate(this.baseShiftTemplateData).subscribe((data) => {
          this._snackBarService.callSnackBar(data, "Successfully entered");
          if (data == AdministrationResultStatus.Success) {
            this.closeShiftPopup();
          } else {
            this._snackBarService.callSnackBar('Error', "Error");
          }
        });
      } else {

        let updatedShiftTemplateData = new UpdateShiftTemplateData({
          scheduleId: this.originalEditingScheduleData?.scheduleId,
          name: this.baseShiftTemplateData.name,
          description: this.baseShiftTemplateData.description,
          startingFrom: this.baseShiftTemplateData.startingFrom,
          endOn: this.baseShiftTemplateData.endOn,
          repeating: this.baseShiftTemplateData.repeating,
          workWeekList: [],
        });

        currentWorkWeekConfigs.forEach((weekConfig) => {
          weekConfig.workWeekConfigData.workDaysConfig?.forEach((dayConfig) => {
            dayConfig.fromTime = moment(dayConfig.fromTime).utc(true).toDate();
            dayConfig.toTime = moment(dayConfig.toTime).utc(true).toDate();
          });
          updatedShiftTemplateData.workWeekList?.push(weekConfig.workWeekConfigData);
        });

        this.shiftTemplateService.updateShiftTemplate(updatedShiftTemplateData).subscribe((data) => {
          if (data == AdministrationResultStatus.Success) {
            this.closeShiftPopup();
          } else {
            this._snackBarService.callSnackBar('Error', "Error");
          }
        });
      }
    }
  }

  public updateFromTimeAddingList(value: string, workingHourConfig: WorkingHoursDailyConfigData, inputElement: HTMLInputElement) {
    if (workingHourConfig.working) {
      const enteredTime = new Date(`1970-01-01T${value}:00`);
      const minTime = this.getMinFromTimeForDay(workingHourConfig.dayOfWeek!);
      const maxTime = this.getMaxToTimeForDay(workingHourConfig.dayOfWeek!);

      if (enteredTime < minTime) {
        const minTimeString = this.formatTime(minTime);
        if (inputElement) { inputElement.value = minTimeString; }
        workingHourConfig.fromTime = minTime;
        return;
      } else if (enteredTime > maxTime) {
        const maxTimeString = this.formatTime(maxTime);
        if (inputElement) { inputElement.value = maxTimeString; }
        workingHourConfig.fromTime = maxTime;
        return;
      }

      workingHourConfig.fromTime?.setHours(parseInt(value.split(':')[0]));
      workingHourConfig.fromTime?.setMinutes(parseInt(value.split(':')[1]));

      if (workingHourConfig && workingHourConfig.toTime && workingHourConfig.fromTime) {
        if (workingHourConfig.fromTime > workingHourConfig.toTime) {
          let originalMinutes = workingHourConfig.toTime.getMinutes();
          workingHourConfig.toTime = new Date(workingHourConfig.fromTime);
          workingHourConfig.toTime.setHours(
            workingHourConfig.toTime.getHours() + 1
          );
          workingHourConfig.toTime.setMinutes(originalMinutes);
        }
      }
    }
  }

  public updateToTimeAddingList(value: string, workingHourConfig: WorkingHoursDailyConfigData, inputElement: HTMLInputElement) {
    if (workingHourConfig.working) {
      const enteredTime = new Date(`1970-01-01T${value}:00`);
      const minTime = this.getMinFromTimeForDay(workingHourConfig.dayOfWeek!);
      const maxTime = this.getMaxToTimeForDay(workingHourConfig.dayOfWeek!);

      if (enteredTime > maxTime) {
        const maxTimeString = this.formatTime(maxTime);
        if (inputElement) { inputElement.value = maxTimeString; }
        workingHourConfig.toTime = maxTime;
        return;
      } else if (enteredTime < minTime) {
        const minTimeString = this.formatTime(minTime);
        if (inputElement) { inputElement.value = minTimeString; }
        workingHourConfig.toTime = minTime;
        return;
      }

      workingHourConfig.toTime?.setHours(parseInt(value.split(':')[0]));
      workingHourConfig.toTime?.setMinutes(parseInt(value.split(':')[1]));

      if (workingHourConfig && workingHourConfig.toTime && workingHourConfig.fromTime) {
        if (workingHourConfig.toTime < workingHourConfig.fromTime) {
          let originalMinutes = workingHourConfig.fromTime.getMinutes();
          workingHourConfig.fromTime = new Date(workingHourConfig.toTime);
          workingHourConfig.fromTime.setHours(
            workingHourConfig.fromTime.getHours() - 1
          );
          workingHourConfig.fromTime.setMinutes(originalMinutes);
        }
      }
    }
  }

  private getMaxToTimeForDay(dayOfWeek: number): Date {
    const maxTimeConfig = this.baseWorkWeekConfig.workDaysConfig?.find(config => config.dayOfWeek === dayOfWeek);
    if (maxTimeConfig) {
      return maxTimeConfig.toTime!;
    }
    return new Date('1970-01-01T23:59:59');
  }

  private getMinFromTimeForDay(dayOfWeek: number): Date {
    const minTimeConfig = this.baseWorkWeekConfig.workDaysConfig?.find(config => config.dayOfWeek === dayOfWeek);
    if (minTimeConfig) {
      return minTimeConfig.fromTime!;
    }
    return new Date('1970-01-01T00:00:00');
  }

  private formatTime(time: Date): string {
    return `${('0' + time.getHours()).slice(-2)}:${('0' + time.getMinutes()).slice(-2)}`;
  }

  public scrollToEnd() {
    if (this.overflowContainer) {
      const container = this.overflowContainer.nativeElement;
      const startPosition = container.scrollLeft;
      const endPosition = container.scrollWidth - container.clientWidth;
      const duration = DEFAULT_SCROLL_DURATION;
      const startTime = performance.now();

      function scroll(time: number) {
        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1);
        container.scrollLeft = startPosition + (endPosition - startPosition) * progress;

        if (progress < 1) {
          requestAnimationFrame(scroll);
        }
      }

      requestAnimationFrame(scroll);
    }
  }

  public translate(key: string) {
    return this.translateService.instant(key);
  }
}

